import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Mail, Clock, Loader2, BellOff, Bell } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

// A short, curated list. We can extend later.
const TIMEZONES = [
  "America/Mexico_City", "America/Bogota", "America/Lima", "America/Santiago",
  "America/Buenos_Aires", "America/New_York", "America/Los_Angeles",
  "America/Chicago", "Europe/Madrid", "Europe/London", "UTC",
];

function detectBrowserTimezone() {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || null; }
  catch { return null; }
}

/**
 * Board digest settings modal — creator only can edit. Members see read-only.
 * Members also see a self opt-out switch to stop receiving digests for this board.
 */
export default function BoardNotifSettingsDialog({ boardId, open, onOpenChange }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["board-notifs", boardId],
    queryFn: async () => (await api.get(`/boards/${boardId}/notifications`)).data,
    enabled: open && !!boardId,
  });

  const [form, setForm] = useState(null);
  useEffect(() => {
    if (!data) return;
    // If the board has never set a timezone, autodetect from the browser.
    // Default backend value is America/Mexico_City → only autodetect when the
    // detected TZ differs AND `last_sent_at` is null (fresh config).
    const detected = detectBrowserTimezone();
    const isFresh = !data.last_sent_at;
    let tz = data.timezone;
    if (data.is_creator && isFresh && detected && detected !== tz) {
      tz = detected;
    }
    setForm({ ...data, timezone: tz });
  }, [data]);

  // Ensure the dropdown always contains the currently selected TZ,
  // even if it isn't in the curated list.
  const availableTZs = form?.timezone && !TIMEZONES.includes(form.timezone)
    ? [form.timezone, ...TIMEZONES]
    : TIMEZONES;

  const save = useMutation({
    mutationFn: async () => (await api.patch(`/boards/${boardId}/notifications`, {
      enabled: form.enabled,
      frequency: form.frequency,
      hour: Number(form.hour),
      timezone: form.timezone,
    })).data,
    onSuccess: () => {
      toast.success(t("boardNotif.saved", "Preferencias guardadas"));
      qc.invalidateQueries({ queryKey: ["board-notifs", boardId] });
      qc.invalidateQueries({ queryKey: ["my-board-notifs"] });
      onOpenChange(false);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || t("common.error", "Error")),
  });

  const toggleSelf = useMutation({
    mutationFn: async (subscribe) =>
      subscribe
        ? (await api.delete(`/boards/${boardId}/notifications/unsubscribe`)).data
        : (await api.post(`/boards/${boardId}/notifications/unsubscribe`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["board-notifs", boardId] });
      toast.success(t("boardNotif.selfSaved", "Preferencia actualizada"));
    },
    onError: (e) => toast.error(e?.response?.data?.detail || t("common.error", "Error")),
  });

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="board-notif-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-blue-500" />
            {t("boardNotif.title", "Notificaciones por email")}
          </DialogTitle>
          <DialogDescription>
            {t("boardNotif.desc", "Envía un resumen periódico a los integrantes del tablero con sus pendientes y próximas fechas.")}
          </DialogDescription>
        </DialogHeader>

        {isLoading || !form ? (
          <div className="py-10 grid place-items-center">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-5 py-2">
            {/* Creator controls */}
            {form.is_creator ? (
              <>
                <div className="flex items-center justify-between rounded-xl bg-white/40 dark:bg-white/5 border border-white/40 dark:border-white/5 px-4 py-3">
                  <div>
                    <Label className="font-medium">{t("boardNotif.enable", "Activar resúmenes")}</Label>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {t("boardNotif.enableHint", "Envía correos automáticos a todos los integrantes.")}
                    </p>
                  </div>
                  <Switch
                    checked={!!form.enabled}
                    onCheckedChange={(v) => setForm({ ...form, enabled: v })}
                    data-testid="board-notif-enabled"
                  />
                </div>

                <div className={form.enabled ? "space-y-4" : "space-y-4 opacity-50 pointer-events-none"}>
                  <div className="space-y-1.5">
                    <Label>{t("boardNotif.frequency", "Frecuencia")}</Label>
                    <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
                      <SelectTrigger data-testid="board-notif-frequency"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily">{t("boardNotif.daily", "Diaria")}</SelectItem>
                        <SelectItem value="weekly">{t("boardNotif.weekly", "Semanal (lunes 8 AM)")}</SelectItem>
                        <SelectItem value="monthly">{t("boardNotif.monthly", "Mensual (día 1, 8 AM)")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {form.frequency === "daily" && (
                    <div className="space-y-1.5">
                      <Label className="flex items-center gap-1.5">
                        <Clock className="h-3.5 w-3.5" /> {t("boardNotif.hour", "Hora de envío (0-23)")}
                      </Label>
                      <Select value={String(form.hour)} onValueChange={(v) => setForm({ ...form, hour: Number(v) })}>
                        <SelectTrigger data-testid="board-notif-hour"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: 24 }).map((_, h) => (
                            <SelectItem key={h} value={String(h)}>{String(h).padStart(2, "0")}:00</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div className="space-y-1.5">
                    <Label>{t("boardNotif.timezone", "Zona horaria")}</Label>
                    <Select value={form.timezone} onValueChange={(v) => setForm({ ...form, timezone: v })}>
                      <SelectTrigger data-testid="board-notif-timezone"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {availableTZs.map(tz => <SelectItem key={tz} value={tz}>{tz}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {form.timezone === detectBrowserTimezone() && (
                      <p className="text-[11px] text-muted-foreground mt-1">
                        {t("boardNotif.tzAutodetected", "Detectada automáticamente desde tu navegador.")}
                      </p>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <div className="rounded-xl bg-slate-500/5 border border-slate-500/10 px-4 py-3 text-sm text-muted-foreground">
                {form.enabled ? (
                  <>
                    {t("boardNotif.readOnlyOn", "El creador tiene activado el envío ")}
                    <strong>{form.frequency === "daily" ? t("boardNotif.daily", "Diaria")
                          : form.frequency === "weekly" ? t("boardNotif.weekly", "Semanal (lunes 8 AM)")
                          : t("boardNotif.monthly", "Mensual (día 1, 8 AM)")}</strong>.
                  </>
                ) : (
                  t("boardNotif.readOnlyOff", "El creador tiene los resúmenes por email desactivados.")
                )}
              </div>
            )}

            {/* Self opt-out (any member) */}
            <div className="border-t border-white/10 pt-4">
              <div className="flex items-center justify-between rounded-xl bg-white/40 dark:bg-white/5 border border-white/40 dark:border-white/5 px-4 py-3">
                <div className="flex items-start gap-3">
                  {form.user_subscribed ? <Bell className="h-5 w-5 text-blue-500 mt-0.5" /> : <BellOff className="h-5 w-5 text-muted-foreground mt-0.5" />}
                  <div>
                    <Label className="font-medium">
                      {t("boardNotif.mySub", "Recibir estos correos")}
                    </Label>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {t("boardNotif.mySubHint", "Aunque el creador los active, puedes silenciar este tablero.")}
                    </p>
                  </div>
                </div>
                <Switch
                  checked={form.user_subscribed}
                  onCheckedChange={(v) => toggleSelf.mutate(v)}
                  disabled={toggleSelf.isPending}
                  data-testid="board-notif-self"
                />
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>{t("common.close", "Cerrar")}</Button>
          {form?.is_creator && (
            <Button
              className="bg-blue-600 hover:bg-blue-700 text-white"
              disabled={save.isPending}
              onClick={() => save.mutate()}
              data-testid="board-notif-save"
            >
              {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : t("common.save", "Guardar")}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
